import React, { useState, useRef, useEffect } from 'react';
import FeedView from './FeedView';
import { Upload, Video, Copy, Check, Play, X, User, LogOut, Film, Tag, Search, Edit, Trash2, FileVideo, ChevronDown, ChevronUp, Megaphone } from 'lucide-react';
import AdminPanel from './AdminPanel';
import AdminAds from './AdminAds';
import UpgradeModal from './UpgradeModal';
import TagExplorer from './TagExplorer';
import TagAutocompleteInput from './TagAutocompleteInput';
import VideoAd from './VideoAd';

const API_URL = process.env.REACT_APP_API_URL || '';
const LIMIT = 24;

// Hoisted to module scope (outside App) so they keep a stable component
// identity across renders. When they were declared inside App's body,
// every App re-render (i.e. every keystroke in a form field) redefined them
// as a brand-new function, so React unmounted/remounted the whole modal
// subtree on every keystroke — dropping input focus and, for EditModal,
// silently resetting any unsaved defaultValue edits back to the original.
function BulkUploadModal({
  queuedVideos,
  expandedVideos,
  fileInputRef,
  summary,
  onClose,
  onDismissSummary,
  onSelectMore,
  onFilesDropped,
  onUpdateMetadata,
  onAddTag,
  onRemoveTag,
  onRemoveVideo,
  onToggleExpand,
  onUploadAll,
  onCancelUpload
}) {
  const [isDragging, setIsDragging] = useState(false);
  const [confirmingClose, setConfirmingClose] = useState(false);

  const pendingCount = queuedVideos.filter(v => v.status === 'pending').length;
  const uploadingCount = queuedVideos.filter(v => v.status === 'uploading').length;
  const processingCount = queuedVideos.filter(v => v.status === 'processing').length;
  const completeCount = queuedVideos.filter(v => v.status === 'complete').length;
  const errorCount = queuedVideos.filter(v => v.status === 'error').length;
  const activeCount = uploadingCount + processingCount;
  const hasEmptyTitle = queuedVideos.some(v => v.status === 'pending' && !v.title.trim());

  const requestClose = () => {
    if (activeCount > 0) {
      setConfirmingClose(true);
      return;
    }
    onClose();
  };

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') requestClose();
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeCount]);

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };
  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragging(false);
  };
  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    if (e.dataTransfer.files?.length) onFilesDropped(e.dataTransfer.files);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
      <div
        className="bg-zinc-900 rounded-lg max-w-4xl w-full max-h-[90vh] flex flex-col"
        role="dialog"
        aria-modal="true"
        aria-labelledby="bulk-upload-title"
      >
        {/* Header */}
        <div className="flex justify-between items-center p-6 border-b border-zinc-800">
          <div>
            <h2 id="bulk-upload-title" className="text-2xl font-bold">Bulk Upload</h2>
            <p className="text-sm text-zinc-400 mt-1" aria-live="polite">
              {pendingCount} pending • {uploadingCount} uploading • {processingCount} processing • {completeCount} complete • {errorCount} failed
            </p>
          </div>
          <button onClick={requestClose} aria-label="Close upload dialog" className="p-1 hover:bg-zinc-800 rounded">
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Inline close-confirmation, replaces window.confirm() */}
        {confirmingClose && (
          <div className="flex items-center justify-between gap-3 px-6 py-3 bg-yellow-900/40 border-b border-yellow-700 text-sm">
            <span>Uploads are still in progress. Close anyway?</span>
            <div className="flex gap-2 shrink-0">
              <button
                onClick={() => setConfirmingClose(false)}
                className="px-3 py-1 rounded bg-zinc-700 hover:bg-zinc-600"
              >
                Keep uploading
              </button>
              <button
                onClick={onClose}
                className="px-3 py-1 rounded bg-red-600 hover:bg-red-700"
              >
                Close anyway
              </button>
            </div>
          </div>
        )}

        {/* Completion summary, replaces alert() */}
        {summary && (
          <div
            className={`flex items-center justify-between gap-3 px-6 py-3 border-b text-sm ${
              summary.failed > 0 ? 'bg-yellow-900/40 border-yellow-700' : 'bg-green-900/40 border-green-700'
            }`}
            aria-live="polite"
          >
            <span>
              Upload complete — {summary.successful} succeeded{summary.failed > 0 ? `, ${summary.failed} failed` : ''}.
            </span>
            <button onClick={onDismissSummary} aria-label="Dismiss summary" className="p-1 hover:bg-black/20 rounded">
              <X className="w-4 h-4" />
            </button>
          </div>
        )}

        {/* Video List */}
        <div
          className="flex-1 overflow-y-auto p-6"
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
        >
          {queuedVideos.length === 0 ? (
            <div
              className={`text-center py-12 rounded-lg border-2 border-dashed transition-colors ${
                isDragging ? 'border-red-500 bg-red-500/5 text-red-300' : 'border-zinc-800 text-zinc-400'
              }`}
            >
              <FileVideo className="w-16 h-16 mx-auto mb-4 opacity-50" />
              <p>{isDragging ? 'Drop to add videos' : 'No videos selected'}</p>
              <p className="text-xs text-zinc-500 mt-1">Drag and drop video files here, or</p>
              <button
                onClick={() => fileInputRef.current?.click()}
                className="mt-4 px-6 py-2 bg-red-600 hover:bg-red-700 rounded-lg transition-colors"
              >
                Select Videos
              </button>
            </div>
          ) : (
            <div className={`space-y-3 rounded-lg ${isDragging ? 'ring-2 ring-red-500 ring-offset-2 ring-offset-zinc-900' : ''}`}>
              {queuedVideos.map((video) => (
                <div
                  key={video.id}
                  className={`bg-zinc-800 rounded-lg p-4 ${
                    video.status === 'error' ? 'border-2 border-red-500' :
                    video.status === 'complete' ? 'border-2 border-green-500' : ''
                  }`}
                >
                  {/* Video Header */}
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-3 flex-1">
                      <FileVideo className="w-5 h-5 text-red-500" />
                      <div className="flex-1 min-w-0">
                        <p className="font-medium truncate">{video.filename}</p>
                        <p className="text-xs text-zinc-400">{video.size} MB</p>
                      </div>
                      <div className="flex items-center gap-2">
                        {video.status === 'complete' && (
                          <span className="text-green-500 text-sm">✓ Complete</span>
                        )}
                        {video.status === 'error' && (
                          <span className="text-red-500 text-sm">✗ {video.error === 'Cancelled' ? 'Cancelled' : 'Failed'}</span>
                        )}
                        {video.status === 'uploading' && (
                          <span className="text-blue-500 text-sm" aria-live="polite">{video.progress}%</span>
                        )}
                        {video.status === 'processing' && (
                          <span className="text-purple-400 text-sm" aria-live="polite">Processing…</span>
                        )}
                        <button
                          onClick={() => onToggleExpand(video.id)}
                          aria-label={expandedVideos[video.id] ? `Collapse details for ${video.filename}` : `Expand details for ${video.filename}`}
                          className="p-1 hover:bg-zinc-700 rounded"
                        >
                          {expandedVideos[video.id] ? (
                            <ChevronUp className="w-4 h-4" />
                          ) : (
                            <ChevronDown className="w-4 h-4" />
                          )}
                        </button>
                        {(video.status === 'uploading' || video.status === 'processing') && (
                          <button
                            onClick={() => onCancelUpload(video.id)}
                            aria-label={`Cancel upload of ${video.filename}`}
                            className="p-1 hover:bg-red-700 rounded text-red-500"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        )}
                        {(video.status === 'pending' || video.status === 'error') && (
                          <button
                            onClick={() => onRemoveVideo(video.id)}
                            aria-label={`Remove ${video.filename} from queue`}
                            className="p-1 hover:bg-red-700 rounded text-red-500"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Progress Bar */}
                  {video.status === 'uploading' && (
                    <div
                      className="w-full bg-zinc-700 rounded-full h-2 mb-2"
                      role="progressbar"
                      aria-valuenow={video.progress}
                      aria-valuemin={0}
                      aria-valuemax={100}
                    >
                      <div
                        className="bg-red-600 h-2 rounded-full transition-all"
                        style={{ width: `${video.progress}%` }}
                      />
                    </div>
                  )}
                  {video.status === 'processing' && (
                    <div className="w-full bg-zinc-700 rounded-full h-2 mb-2 overflow-hidden">
                      <div className="bg-purple-500 h-2 rounded-full w-1/3 animate-pulse" />
                    </div>
                  )}

                  {/* Error Message */}
                  {video.error && (
                    <div className="text-sm text-red-400 mb-2">
                      {video.error === 'Cancelled' ? 'Cancelled by user' : `Error: ${video.error}`}
                    </div>
                  )}

                  {/* Metadata Form (Expanded) */}
                  {expandedVideos[video.id] && video.status === 'pending' && (
                    <div className="mt-3 space-y-3 pt-3 border-t border-zinc-700">
                      <div>
                        <input
                          type="text"
                          placeholder="Title *"
                          value={video.title}
                          onChange={(e) => onUpdateMetadata(video.id, 'title', e.target.value)}
                          aria-required="true"
                          className={`w-full px-3 py-2 bg-zinc-700 rounded border outline-none text-sm ${
                            !video.title.trim() ? 'border-red-500' : 'border-zinc-600 focus:border-red-500'
                          }`}
                        />
                        {!video.title.trim() && (
                          <p className="text-xs text-red-400 mt-1">Title is required before this video can be uploaded.</p>
                        )}
                      </div>

                      <div className="grid grid-cols-2 gap-3">
                        <input
                          type="text"
                          placeholder="Actress"
                          value={video.actress}
                          onChange={(e) => onUpdateMetadata(video.id, 'actress', e.target.value)}
                          className="w-full px-3 py-2 bg-zinc-700 rounded border border-zinc-600 focus:border-red-500 outline-none text-sm"
                        />
                        <input
                          type="text"
                          placeholder="Scene"
                          value={video.scene}
                          onChange={(e) => onUpdateMetadata(video.id, 'scene', e.target.value)}
                          className="w-full px-3 py-2 bg-zinc-700 rounded border border-zinc-600 focus:border-red-500 outline-none text-sm"
                        />
                      </div>

                      <div className="grid grid-cols-2 gap-3">
                        <input
                          type="text"
                          placeholder="Website"
                          value={video.website}
                          onChange={(e) => onUpdateMetadata(video.id, 'website', e.target.value)}
                          className="w-full px-3 py-2 bg-zinc-700 rounded border border-zinc-600 focus:border-red-500 outline-none text-sm"
                        />
                        <select
                          value={video.category}
                          onChange={(e) => onUpdateMetadata(video.id, 'category', e.target.value)}
                          className="w-full px-3 py-2 bg-zinc-700 rounded border border-zinc-600 focus:border-red-500 outline-none text-sm"
                        >
                          <option value="general">General</option>
                          <option value="hardcore">Hardcore</option>
                          <option value="softcore">Softcore</option>
                          <option value="amateur">Amateur</option>
                          <option value="professional">Professional</option>
                          <option value="vintage">Vintage</option>
                        </select>
                      </div>

                      {/* Visibility */}
                      <div className="flex items-center gap-3 text-sm">
                        <span className="text-zinc-400">Visibility:</span>
                        <button
                          type="button"
                          onClick={() => onUpdateMetadata(video.id, 'is_public', true)}
                          className={`px-3 py-1 rounded transition-colors ${
                            video.is_public ? 'bg-red-600 text-white' : 'bg-zinc-700 text-zinc-300 hover:bg-zinc-600'
                          }`}
                        >
                          Public
                        </button>
                        <button
                          type="button"
                          onClick={() => onUpdateMetadata(video.id, 'is_public', false)}
                          className={`px-3 py-1 rounded transition-colors ${
                            !video.is_public ? 'bg-red-600 text-white' : 'bg-zinc-700 text-zinc-300 hover:bg-zinc-600'
                          }`}
                        >
                          Unlisted
                        </button>
                      </div>

                      {/* Tags */}
                      <TagAutocompleteInput
                        existingTags={video.tags}
                        onAdd={(tag) => onAddTag(video.id, tag)}
                        onRemove={(tag) => onRemoveTag(video.id, tag)}
                        inputClassName="flex-1 px-3 py-2 bg-zinc-700 rounded border border-zinc-600 focus:border-red-500 outline-none text-sm"
                        addButtonClassName="px-3 py-2 bg-zinc-600 hover:bg-zinc-500 rounded text-sm"
                      />
                      {video.tags.length > 0 && (
                        <div className="flex flex-wrap gap-1">
                          {video.tags.map((tag, idx) => (
                            <span
                              key={idx}
                              className="bg-red-600 px-2 py-1 rounded text-xs flex items-center gap-1"
                            >
                              {tag}
                              <button
                                onClick={() => onRemoveTag(video.id, tag)}
                                aria-label={`Remove tag ${tag}`}
                                className="hover:bg-red-700 rounded"
                              >
                                <X className="w-3 h-3" />
                              </button>
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        {queuedVideos.length > 0 && (
          <div className="border-t border-zinc-800">
            {hasEmptyTitle && activeCount === 0 && pendingCount > 0 && (
              <p className="text-xs text-yellow-400 px-6 pt-3">
                Add a title to every pending video before uploading.
              </p>
            )}
            <div className="p-6 flex gap-3">
              <button
                onClick={onSelectMore}
                disabled={activeCount > 0}
                className="px-6 py-3 bg-zinc-700 hover:bg-zinc-600 rounded-lg font-semibold transition-colors disabled:opacity-50"
              >
                Add More Videos
              </button>
              <button
                onClick={onUploadAll}
                disabled={pendingCount === 0 || activeCount > 0 || hasEmptyTitle}
                className="flex-1 px-6 py-3 bg-red-600 hover:bg-red-700 disabled:bg-zinc-700 rounded-lg font-semibold transition-colors"
              >
                {activeCount > 0 ? `Uploading... (${activeCount})` : `Upload All (${pendingCount})`}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function EditModal({ editingVideo, editTags, editIsPublic, loading, error, onClose, onSave, onAddTag, onRemoveTag, onToggleIsPublic }) {
  return (
    <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4 overflow-y-auto">
      <div className="bg-zinc-900 rounded-lg max-w-2xl w-full p-6 my-8">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-2xl font-bold">Edit Video</h2>
          <button onClick={() => !loading && onClose()}>
            <X className="w-6 h-6" />
          </button>
        </div>

        {error && (
          <div className="bg-red-900 border border-red-700 text-red-100 px-4 py-3 rounded mb-4">
            {error}
          </div>
        )}

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium mb-2">Video Title *</label>
            <input
              id="edit-video-title"
              type="text"
              defaultValue={editingVideo?.title}
              disabled={loading}
              className="w-full px-4 py-3 bg-zinc-800 rounded border border-zinc-700 focus:border-red-500 outline-none text-white disabled:opacity-50"
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-2">Actress/Model</label>
            <input
              id="edit-video-actress"
              type="text"
              defaultValue={editingVideo?.actress || ''}
              disabled={loading}
              className="w-full px-4 py-3 bg-zinc-800 rounded border border-zinc-700 focus:border-red-500 outline-none text-white disabled:opacity-50"
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-2">Scene Name</label>
            <input
              id="edit-video-scene"
              type="text"
              defaultValue={editingVideo?.scene || ''}
              disabled={loading}
              className="w-full px-4 py-3 bg-zinc-800 rounded border border-zinc-700 focus:border-red-500 outline-none text-white disabled:opacity-50"
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-2">Source Website</label>
            <input
              id="edit-video-website"
              type="text"
              defaultValue={editingVideo?.website || ''}
              disabled={loading}
              className="w-full px-4 py-3 bg-zinc-800 rounded border border-zinc-700 focus:border-red-500 outline-none text-white disabled:opacity-50"
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-2">Category</label>
            <select
              id="edit-video-category"
              defaultValue={editingVideo?.category || 'general'}
              disabled={loading}
              className="w-full px-4 py-3 bg-zinc-800 rounded border border-zinc-700 focus:border-red-500 outline-none text-white disabled:opacity-50"
            >
              <option value="general">General</option>
              <option value="hardcore">Hardcore</option>
              <option value="softcore">Softcore</option>
              <option value="amateur">Amateur</option>
              <option value="professional">Professional</option>
              <option value="vintage">Vintage</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium mb-2 flex items-center gap-2">
              <Tag className="w-4 h-4" />
              Tags
            </label>
            <TagAutocompleteInput
              existingTags={editTags}
              onAdd={onAddTag}
              onRemove={onRemoveTag}
              disabled={loading}
              inputClassName="flex-1 px-4 py-3 bg-zinc-800 rounded border border-zinc-700 focus:border-red-500 outline-none text-white disabled:opacity-50"
              addButtonClassName="px-4 py-3 bg-zinc-700 hover:bg-zinc-600 rounded disabled:opacity-50 transition-colors"
            />
            {editTags.length > 0 && (
              <div className="flex flex-wrap gap-2">
                {editTags.map((tag, idx) => (
                  <span
                    key={idx}
                    className="bg-red-600 px-3 py-1 rounded-full text-sm flex items-center gap-2"
                  >
                    {tag}
                    <button
                      onClick={() => onRemoveTag(tag)}
                      className="hover:bg-red-700 rounded-full p-0.5"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </span>
                ))}
              </div>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium mb-2">Visibility</label>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => onToggleIsPublic(true)}
                disabled={loading}
                className={`flex-1 px-4 py-3 rounded border font-medium transition-colors disabled:opacity-50 ${
                  editIsPublic
                    ? 'bg-red-600 border-red-600 text-white'
                    : 'bg-zinc-800 border-zinc-700 text-zinc-300 hover:bg-zinc-700'
                }`}
              >
                Public
              </button>
              <button
                type="button"
                onClick={() => onToggleIsPublic(false)}
                disabled={loading}
                className={`flex-1 px-4 py-3 rounded border font-medium transition-colors disabled:opacity-50 ${
                  !editIsPublic
                    ? 'bg-red-600 border-red-600 text-white'
                    : 'bg-zinc-800 border-zinc-700 text-zinc-300 hover:bg-zinc-700'
                }`}
              >
                Unlisted
              </button>
            </div>
            <p className="text-xs text-zinc-500 mt-1">
              {editIsPublic
                ? 'Shown in the feed, search, and tag pages.'
                : 'Hidden from feed/search/tags — only visible to people with the direct link.'}
            </p>
          </div>

          <button
            onClick={onSave}
            disabled={loading}
            className="w-full bg-blue-600 hover:bg-blue-700 disabled:bg-zinc-700 py-4 rounded-lg font-semibold transition-colors"
          >
            {loading ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </div>
    </div>
  );
}

// Hoisted to module scope for the same reason as BulkUploadModal/EditModal
// above: a stable component identity keeps the ad's impression-tracking
// effect from re-firing on every unrelated App re-render.
function VideoPlayerModal({ video, onClose, copiedId, onCopy }) {
  const [adReady, setAdReady] = useState(false);

  useEffect(() => {
    setAdReady(false);
  }, [video?.id]);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-95 flex items-center justify-center z-50 p-4">
      <div className="max-w-5xl w-full">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-2xl font-bold">{video?.title}</h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-zinc-800 rounded-full transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        <VideoAd key={video?.id} onReady={() => setAdReady(true)} />

        {adReady && (
          <div className="bg-black rounded-lg overflow-hidden">
            <video
              controls
              autoPlay
              className="w-full"
              src={`${API_URL}/api/videos/${video?.id}/stream`}
            >
              Your browser does not support video playback.
            </video>
          </div>
        )}

        <div className="mt-4 space-y-2">
          <div className="flex items-center justify-between text-sm text-zinc-400">
            <span>@{video?.username}</span>
            <span>{video?.views || 0} views • {video?.quality}</span>
          </div>

          {video?.actress && (
            <div className="text-sm text-red-400">🎭 {video.actress}</div>
          )}

          {video?.scene && (
            <div className="text-sm text-zinc-400">🎬 {video.scene}</div>
          )}

          {video?.website && (
            <div className="text-sm text-zinc-400">🌐 {video.website}</div>
          )}
        </div>

        {video?.tags && video.tags.length > 0 && (
          <div className="mt-4 flex flex-wrap gap-2">
            {video.tags.map((tag, idx) => (
              <span key={idx} className="text-sm bg-zinc-800 px-3 py-1 rounded-full">
                {tag}
              </span>
            ))}
          </div>
        )}

        <button
          onClick={() => onCopy(video?.share_url, video?.id)}
          className="mt-4 w-full flex items-center justify-center gap-2 px-4 py-3 bg-red-600 hover:bg-red-700 rounded-lg font-semibold transition-colors"
        >
          {copiedId === video?.id ? (
            <>
              <Check className="w-5 h-5" />
              Link Copied!
            </>
          ) : (
            <>
              <Copy className="w-5 h-5" />
              Share Video
            </>
          )}
        </button>
      </div>
    </div>
  );
}

const App = () => {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [showPlayerModal, setShowPlayerModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedVideo, setSelectedVideo] = useState(null);
  const [editingVideo, setEditingVideo] = useState(null);
  const [authMode, setAuthMode] = useState('login');
  const [videos, setVideos] = useState([]);
  const [copiedId, setCopiedId] = useState(null);
  const [showMenu, setShowMenu] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [editTags, setEditTags] = useState([]);
  const [editIsPublic, setEditIsPublic] = useState(true);

  // Pagination / infinite scroll
  const [showFeed, setShowFeed] = useState(false);
  const [showAdmin, setShowAdmin] = useState(false);
  const [showAdsAdmin, setShowAdsAdmin] = useState(false);
  const [showExplore, setShowExplore] = useState(false);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [pendingUpgradeAfterAuth, setPendingUpgradeAfterAuth] = useState(false);
  const [feedVideos, setFeedVideos] = useState([]);
  const [feedFilter, setFeedFilter] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const loaderRef = useRef(null);

  // Bulk upload states
  const [queuedVideos, setQueuedVideos] = useState([]);
  const [expandedVideos, setExpandedVideos] = useState({});
  const [uploadSummary, setUploadSummary] = useState(null); // { successful, failed } | null

  const fileInputRef = useRef(null);
  const uploadXhrRefs = useRef({}); // videoId -> in-flight XMLHttpRequest, for per-file cancel

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      fetchCurrentUser(token);
    }
    fetchVideos(1, false);

    // Landed here via the "Upgrade to Premium" ad click-through (click_url is /upgrade).
    // No router in this app, so this is a one-shot check of the initial URL.
    if (window.location.pathname === '/upgrade') {
      window.history.replaceState(null, '', '/');
      if (token) {
        setShowUpgradeModal(true);
      } else {
        setAuthMode('signup');
        setShowAuthModal(true);
        setPendingUpgradeAfterAuth(true);
      }
    }
  }, []);

// Open de feed met een filter (tag of zoekterm). Haalt een eigen lijst op,
  // laat de gallery-videos ongemoeid.
  const openFeedWithFilter = async (filter) => {
    try {
      const qs = filter.tag
        ? `tag=${encodeURIComponent(filter.tag)}`
        : `search=${encodeURIComponent(filter.search)}`;
      const response = await fetch(`${API_URL}/api/videos?${qs}&page=1&limit=100`);
      const data = await response.json();
      setFeedVideos(data.videos || []);
      setFeedFilter(filter);
      setShowFeed(true);
    } catch (error) {
      console.error('Filter failed:', error);
    }
  };

  // Infinite scroll: load the next page when the sentinel scrolls into view
  useEffect(() => {
    const node = loaderRef.current;
    if (!node) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore && !loadingMore) {
          fetchVideos(page + 1, true);
        }
      },
      { rootMargin: '400px' }
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [page, hasMore, loadingMore]);

  const fetchCurrentUser = async (token) => {
    try {
      const response = await fetch(`${API_URL}/api/auth/me`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setCurrentUser(data);
        setIsLoggedIn(true);
      }
    } catch (error) {
      console.error('Failed to fetch user:', error);
    }
  };

  const fetchVideos = async (pageToLoad = 1, append = false) => {
    try {
      setLoadingMore(true);
      const response = await fetch(`${API_URL}/api/videos?page=${pageToLoad}&limit=${LIMIT}`);
      const data = await response.json();
      const newVideos = data.videos || [];
      setVideos(prev => append ? [...prev, ...newVideos] : newVideos);
      setHasMore(newVideos.length === LIMIT);
      setPage(pageToLoad);
    } catch (error) {
      console.error('Failed to fetch videos:', error);
    } finally {
      setLoadingMore(false);
    }
  };

  // Reload the gallery from page 1 (used after upload/edit/delete)
  const reloadVideos = () => {
    setHasMore(true);
    fetchVideos(1, false);
  };

  const handleAuth = async (email, password, username) => {
    setLoading(true);
    setError('');

    try {
      const endpoint = authMode === 'login' ? '/api/auth/login' : '/api/auth/register';
      const body = authMode === 'login'
        ? { email, password }
        : { username, email, password };

      const response = await fetch(`${API_URL}${endpoint}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
      });

      const data = await response.json();

      if (response.ok) {
        localStorage.setItem('token', data.token);
        setCurrentUser(data.user);
        setIsLoggedIn(true);
        setShowAuthModal(false);
        if (pendingUpgradeAfterAuth) {
          setPendingUpgradeAfterAuth(false);
          setShowUpgradeModal(true);
        }
      } else {
        setError(data.error || 'Authentication failed');
      }
    } catch (error) {
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    setCurrentUser(null);
    setIsLoggedIn(false);
    setShowMenu(false);
  };

  const handleFilesSelected = (files) => {
    const fileArray = Array.from(files);
    const isAdmin = !!currentUser?.is_admin;
    const maxSizeMB = currentUser?.maxSize ?? Infinity;

    // How many upload slots are already spoken for: what the server already
    // has on record, plus anything already sitting in the queue (pending/
    // uploading/complete) from this session.
    const usedSlots = (currentUser?.uploadCount ?? 0) + queuedVideos.filter(
      v => v.status !== 'error'
    ).length;
    const maxSlots = currentUser?.maxUploads ?? Infinity;
    let remainingSlots = isAdmin ? Infinity : Math.max(0, maxSlots - usedSlots);

    const newVideos = fileArray.map((file, index) => {
      const sizeMB = file.size / (1024 * 1024);
      let status = 'pending';
      let error = null;

      if (!isAdmin && sizeMB > maxSizeMB) {
        status = 'error';
        error = `File is ${sizeMB.toFixed(0)}MB, which exceeds your ${maxSizeMB}MB limit.`;
      } else if (!isAdmin && remainingSlots <= 0) {
        status = 'error';
        error = 'Upload limit reached. Upgrade your plan to add more videos.';
      } else {
        remainingSlots--;
      }

      return {
        id: Date.now() + index,
        file: file,
        filename: file.name,
        size: sizeMB.toFixed(2),
        title: file.name.replace(/\.[^/.]+$/, ""), // Remove extension
        actress: '',
        scene: '',
        website: '',
        category: 'general',
        tags: [],
        is_public: true,
        status, // pending, uploading, processing, complete, error
        progress: 0,
        error
      };
    });

    setQueuedVideos([...queuedVideos, ...newVideos]);
    setShowUploadModal(true);
  };

  const updateVideoMetadata = (videoId, field, value) => {
    setQueuedVideos(queuedVideos.map(v =>
      v.id === videoId ? { ...v, [field]: value } : v
    ));
  };

  const addTagToVideo = (videoId, tag) => {
    if (!tag.trim()) return;
    setQueuedVideos(queuedVideos.map(v =>
      v.id === videoId && !v.tags.includes(tag.trim())
        ? { ...v, tags: [...v.tags, tag.trim()] }
        : v
    ));
  };

  const removeTagFromVideo = (videoId, tag) => {
    setQueuedVideos(queuedVideos.map(v =>
      v.id === videoId ? { ...v, tags: v.tags.filter(t => t !== tag) } : v
    ));
  };

  const removeVideoFromQueue = (videoId) => {
    setQueuedVideos(queuedVideos.filter(v => v.id !== videoId));
  };

  const toggleVideoExpanded = (videoId) => {
    setExpandedVideos({
      ...expandedVideos,
      [videoId]: !expandedVideos[videoId]
    });
  };

  const uploadSingleVideo = async (video) => {
    const formData = new FormData();
    formData.append('video', video.file);
    formData.append('title', video.title);
    formData.append('tags', video.tags.join(','));
    formData.append('category', video.category);
    formData.append('actress', video.actress);
    formData.append('scene', video.scene);
    formData.append('website', video.website);
    formData.append('is_public', video.is_public);

    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      uploadXhrRefs.current[video.id] = xhr;

      xhr.upload.addEventListener('progress', (e) => {
        if (e.lengthComputable) {
          const percentComplete = Math.round((e.loaded / e.total) * 100);
          // Once every byte is sent, the browser has nothing left to report —
          // but the server is still transcoding/generating a thumbnail before
          // it responds. Reflect that as a distinct "processing" phase rather
          // than leaving the bar frozen at 100% with no explanation.
          setQueuedVideos(prev => prev.map(v =>
            v.id === video.id
              ? { ...v, progress: percentComplete, status: percentComplete >= 100 ? 'processing' : 'uploading' }
              : v
          ));
        }
      });

      xhr.addEventListener('load', () => {
        delete uploadXhrRefs.current[video.id];
        if (xhr.status === 201) {
          setQueuedVideos(prev => prev.map(v =>
            v.id === video.id ? { ...v, status: 'complete', progress: 100 } : v
          ));
          resolve();
        } else {
          let errorMessage = 'Upload failed';
          try {
            errorMessage = JSON.parse(xhr.responseText).error || errorMessage;
          } catch (e) { /* non-JSON error response */ }
          setQueuedVideos(prev => prev.map(v =>
            v.id === video.id ? { ...v, status: 'error', error: errorMessage } : v
          ));
          reject(new Error(errorMessage));
        }
      });

      xhr.addEventListener('error', () => {
        delete uploadXhrRefs.current[video.id];
        setQueuedVideos(prev => prev.map(v =>
          v.id === video.id ? { ...v, status: 'error', error: 'Network error' } : v
        ));
        reject(new Error('Network error'));
      });

      xhr.addEventListener('abort', () => {
        delete uploadXhrRefs.current[video.id];
        setQueuedVideos(prev => prev.map(v =>
          v.id === video.id ? { ...v, status: 'error', error: 'Cancelled', progress: 0 } : v
        ));
        reject(new Error('Cancelled'));
      });

      xhr.open('POST', `${API_URL}/api/videos/upload`);
      xhr.setRequestHeader('Authorization', `Bearer ${localStorage.getItem('token')}`);
      xhr.send(formData);
    });
  };

  const cancelUpload = (videoId) => {
    uploadXhrRefs.current[videoId]?.abort();
  };

  const UPLOAD_CONCURRENCY = 3;

  const uploadAllVideos = async () => {
    const pendingVideos = queuedVideos.filter(v => v.status === 'pending');
    if (pendingVideos.length === 0) return;

    setUploadSummary(null);

    // Small worker pool instead of one-at-a-time: a handful of videos upload
    // in parallel so a big batch doesn't queue entirely behind the slowest file.
    let nextIndex = 0;
    const worker = async () => {
      while (nextIndex < pendingVideos.length) {
        const video = pendingVideos[nextIndex++];
        try {
          await uploadSingleVideo(video);
        } catch (error) {
          console.error(`Failed to upload ${video.filename}:`, error);
        }
      }
    };
    await Promise.all(
      Array.from({ length: Math.min(UPLOAD_CONCURRENCY, pendingVideos.length) }, worker)
    );

    // Refresh video list
    reloadVideos();

    setQueuedVideos(prev => {
      const successful = prev.filter(v => v.status === 'complete').length;
      const failed = prev.filter(v => v.status === 'error').length;
      setUploadSummary({ successful, failed });
      // Keep failed/cancelled items visible so the user can retry or remove
      // them; drop completed ones from the queue.
      return prev.filter(v => v.status === 'error');
    });
  };

  const addEditTag = (tag) => {
    const newTag = (tag || '').trim();
    if (newTag && !editTags.includes(newTag)) {
      setEditTags([...editTags, newTag]);
    }
  };

  const removeEditTag = (tagToRemove) => {
    setEditTags(editTags.filter(tag => tag !== tagToRemove));
  };

  const handleEditVideo = async () => {
    if (!editingVideo) return;

    setLoading(true);
    setError('');

    const title = document.getElementById('edit-video-title')?.value;
    const actress = document.getElementById('edit-video-actress')?.value || '';
    const scene = document.getElementById('edit-video-scene')?.value || '';
    const website = document.getElementById('edit-video-website')?.value || '';
    const category = document.getElementById('edit-video-category')?.value || 'general';

    try {
      const response = await fetch(`${API_URL}/api/videos/${editingVideo.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          title,
          actress,
          scene,
          website,
          category,
          tags: editTags,
          is_public: editIsPublic
        })
      });

      const data = await response.json();

      if (response.ok) {
        setShowEditModal(false);
        setEditingVideo(null);
        setEditTags([]);
        reloadVideos();
        alert('✅ Video updated successfully!');
      } else {
        setError(data.error || 'Update failed');
      }
    } catch (error) {
      setError('Update failed: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteVideo = async (videoId) => {
    if (!window.confirm('Are you sure you want to delete this video?')) {
      return;
    }

    try {
      const response = await fetch(`${API_URL}/api/videos/${videoId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (response.ok) {
        reloadVideos();
        alert('✅ Video deleted successfully!');
      } else {
        const data = await response.json();
        alert('❌ Delete failed: ' + data.error);
      }
    } catch (error) {
      alert('❌ Delete failed: ' + error.message);
    }
  };

  const openEditModal = (video) => {
    setEditingVideo(video);
    setEditTags(video.tags || []);
    setEditIsPublic(video.is_public !== false);
    setShowEditModal(true);
    setError('');
  };

  const copyToClipboard = (text, videoId) => {
    navigator.clipboard.writeText(text);
    setCopiedId(videoId);
    setTimeout(() => setCopiedId(null), 2000);
  };

  const openVideo = (video) => {
    setSelectedVideo(video);
    setShowPlayerModal(true);
  };

  const VideoCard = ({ video }) => {
    const isOwner = currentUser && (video.user_id === currentUser.id || currentUser.is_admin);
    const [preview, setPreview] = useState(false);
    const previewRef = useRef(null);

    // Start the muted preview as soon as it's mounted on hover
    useEffect(() => {
      if (preview && previewRef.current) {
        previewRef.current.play().catch(() => {});
      }
    }, [preview]);

    return (
      <div className="bg-zinc-900 rounded-lg overflow-hidden hover:ring-2 hover:ring-red-500 transition-all">
        <div
          className="relative aspect-video bg-zinc-800 cursor-pointer group"
          onClick={() => openVideo(video)}
          onMouseEnter={() => setPreview(true)}
          onMouseLeave={() => setPreview(false)}
        >
          <img
            src={`${API_URL}/api/videos/${video.id}/thumbnail`}
            alt={video.title}
            loading="lazy"
            className={`w-full h-full object-cover transition-opacity duration-200 ${preview ? 'opacity-0' : 'opacity-100'}`}
            onError={(e) => {
              e.target.style.display = 'none';
              e.target.nextElementSibling.style.display = 'flex';
            }}
          />
          <div className="w-full h-full hidden items-center justify-center">
            <Film className="w-16 h-16 text-zinc-600" />
          </div>
          {preview && (
            <video
              ref={previewRef}
              src={`${API_URL}/api/videos/${video.id}/stream`}
              muted
              loop
              playsInline
              preload="metadata"
              className="absolute inset-0 w-full h-full object-cover"
            />
          )}
          <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-40 transition-all flex items-center justify-center pointer-events-none">
            <Play className="w-12 h-12 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
          </div>
          <div className="absolute bottom-2 right-2 bg-black bg-opacity-75 px-2 py-1 rounded text-xs pointer-events-none">
            {video.duration}s
          </div>
        </div>
        <div className="p-4">
          <h3 className="font-semibold text-sm mb-1 truncate">{video.title}</h3>
          <div className="text-xs text-zinc-400 mb-3">
            <p>@{video.username} • {video.views || 0} views</p>
            {video.actress && <p className="text-red-400">🎭 {video.actress}</p>}
          </div>
          {video.tags && video.tags.length > 0 && (
            <div className="flex flex-wrap gap-1 mb-3">
              {video.tags.slice(0, 3).map((tag, idx) => (
                <span key={idx} className="text-xs bg-zinc-800 px-2 py-1 rounded">
                  {tag}
                </span>
              ))}
            </div>
          )}

          {isOwner ? (
            <div className="flex gap-2">
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  openEditModal(video);
                }}
                className="flex-1 flex items-center justify-center gap-2 px-3 py-2 bg-blue-600 hover:bg-blue-700 rounded text-sm transition-colors"
              >
                <Edit className="w-4 h-4" />
                Edit
              </button>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handleDeleteVideo(video.id);
                }}
                className="flex items-center justify-center px-3 py-2 bg-red-600 hover:bg-red-700 rounded text-sm transition-colors"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          ) : (
            <button
              onClick={(e) => {
                e.stopPropagation();
                copyToClipboard(video.share_url, video.id);
              }}
              className="w-full flex items-center justify-center gap-2 px-3 py-2 bg-red-600 hover:bg-red-700 rounded text-sm transition-colors"
            >
              {copiedId === video.id ? (
                <>
                  <Check className="w-4 h-4" />
                  Copied!
                </>
              ) : (
                <>
                  <Copy className="w-4 h-4" />
                  Copy Link
                </>
              )}
            </button>
          )}
        </div>
      </div>
    );
  };

  const AuthModal = () => (
    <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
      <div className="bg-zinc-900 rounded-lg max-w-md w-full p-6">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-2xl font-bold">
            {authMode === 'login' ? 'Sign In' : 'Create Account'}
          </h2>
          <button onClick={() => setShowAuthModal(false)}>
            <X className="w-6 h-6" />
          </button>
        </div>

        {error && (
          <div className="bg-red-900 border border-red-700 text-red-100 px-4 py-3 rounded mb-4">
            {error}
          </div>
        )}

        <div className="space-y-4">
          {authMode === 'signup' && (
            <input
              id="signup-username"
              type="text"
              placeholder="Username"
              className="w-full px-4 py-3 bg-zinc-800 rounded border border-zinc-700 focus:border-red-500 outline-none text-white"
            />
          )}
          <input
            id="auth-email"
            type="email"
            placeholder="Email"
            className="w-full px-4 py-3 bg-zinc-800 rounded border border-zinc-700 focus:border-red-500 outline-none text-white"
          />
          <input
            id="auth-password"
            type="password"
            placeholder="Password"
            className="w-full px-4 py-3 bg-zinc-800 rounded border border-zinc-700 focus:border-red-500 outline-none text-white"
          />
          <button
            onClick={() => {
              const email = document.getElementById('auth-email').value;
              const password = document.getElementById('auth-password').value;
              const username = authMode === 'signup' ? document.getElementById('signup-username')?.value : '';
              if (email && password && (authMode === 'login' || username)) {
                handleAuth(email, password, username);
              }
            }}
            disabled={loading}
            className="w-full bg-red-600 hover:bg-red-700 disabled:bg-zinc-700 py-3 rounded font-semibold transition-colors"
          >
            {loading ? 'Loading...' : authMode === 'login' ? 'Sign In' : 'Create Account'}
          </button>
        </div>

        <p className="text-center mt-4 text-sm text-zinc-400">
          {authMode === 'login' ? "Don't have an account? " : 'Already have an account? '}
          <button
            onClick={() => {
              setAuthMode(authMode === 'login' ? 'signup' : 'login');
              setError('');
            }}
            className="text-red-500 hover:underline"
          >
            {authMode === 'login' ? 'Sign up here' : 'Sign in'}
          </button>
        </p>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-zinc-950 text-white">
      {showAdmin && currentUser?.is_admin && (
        <AdminPanel
          token={localStorage.getItem('token')}
          onClose={() => setShowAdmin(false)}
        />
      )}
      {showAdsAdmin && currentUser?.is_admin && (
        <AdminAds
          token={localStorage.getItem('token')}
          onClose={() => setShowAdsAdmin(false)}
        />
      )}
      {showUpgradeModal && (
        <UpgradeModal
          currentUser={currentUser}
          onClose={() => setShowUpgradeModal(false)}
          onNeedAuth={() => {
            setShowUpgradeModal(false);
            setAuthMode('signup');
            setShowAuthModal(true);
            setPendingUpgradeAfterAuth(true);
          }}
          onRequested={(user) => setCurrentUser((prev) => ({ ...prev, ...user }))}
        />
      )}
      {showFeed && (
        <FeedView
          videos={feedFilter ? feedVideos : videos}
          startIndex={0}
          hasMore={feedFilter ? false : hasMore}
          filterLabel={feedFilter ? (feedFilter.tag ? `Tag: ${feedFilter.tag}` : `Zoeken: ${feedFilter.search}`) : null}
          onLoadMore={() => { if (!feedFilter && hasMore && !loadingMore) fetchVideos(page + 1, true); }}
          onClose={() => { setShowFeed(false); setFeedFilter(null); }}
          onTagClick={(tagName) => openFeedWithFilter({ tag: tagName })}
          isPremium={currentUser?.plan === 'premium'}
        />
      )}
      {showExplore && (
        <TagExplorer
          onClose={() => setShowExplore(false)}
          onSelectTag={(name) => { setShowExplore(false); openFeedWithFilter({ tag: name }); }}
          onSearch={(term) => { setShowExplore(false); openFeedWithFilter({ search: term }); }}
        />
      )}
      <header className="bg-zinc-900 border-b border-zinc-800 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Video className="w-8 h-8 text-red-500" />
            <h1 className="text-2xl font-bold">VideoShare</h1>
          </div>

          <div className="flex items-center gap-4">
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter' && searchTerm.trim()) openFeedWithFilter({ search: searchTerm.trim() }); }}
            placeholder="Search among tags…"
            className="hidden sm:block bg-zinc-800 rounded-lg px-3 py-2 text-sm w-40 outline-none focus:ring-1 focus:ring-red-500"
          />
          <button
            onClick={() => setShowFeed(true)}
            className="flex items-center gap-2 px-4 py-2 bg-red-600 hover:bg-red-700 rounded-lg font-semibold transition-colors"
          >
            <Play className="w-4 h-4" />
            Feed
          </button>
          <button
            onClick={() => setShowExplore(true)}
            className="flex items-center gap-2 px-4 py-2 bg-zinc-700 hover:bg-zinc-600 rounded-lg font-semibold transition-colors"
          >
            <Search className="w-4 h-4" />
            Discover tags
          </button>
          {currentUser?.is_admin && (
            <button
              onClick={() => setShowAdmin(true)}
              className="flex items-center gap-2 px-4 py-2 bg-zinc-700 hover:bg-zinc-600 rounded-lg font-semibold transition-colors"
            >
              <Tag className="w-4 h-4" />
              Management
            </button>
          )}
          {currentUser?.is_admin && (
            <button
              onClick={() => setShowAdsAdmin(true)}
              className="flex items-center gap-2 px-4 py-2 bg-zinc-700 hover:bg-zinc-600 rounded-lg font-semibold transition-colors"
            >
              <Megaphone className="w-4 h-4" />
              Ads
            </button>
          )}
	  {isLoggedIn && (
            <button
              onClick={() => {
                setShowUploadModal(true);
                if (queuedVideos.length === 0) {
                  fileInputRef.current?.click();
                }
              }}
              className="flex items-center gap-2 px-4 py-2 bg-red-600 hover:bg-red-700 rounded-lg font-semibold transition-colors"
            >
              <Upload className="w-5 h-5" />
              <span className="hidden md:inline">Upload</span>
            </button>
          )}

            {isLoggedIn ? (
              <div className="relative">
                <button
                  onClick={() => setShowMenu(!showMenu)}
                  className="flex items-center gap-2 px-4 py-2 bg-zinc-800 rounded-lg hover:bg-zinc-700 transition-colors"
                >
                  <User className="w-5 h-5" />
                  <span className="hidden md:inline">{currentUser?.username}</span>
                </button>

                {showMenu && (
                  <div className="absolute right-0 mt-2 w-48 bg-zinc-800 rounded-lg shadow-lg py-2 border border-zinc-700">
                    <div className="px-4 py-2 border-b border-zinc-700">
                      <p className="font-semibold">{currentUser?.username}</p>
                      <p className="text-xs text-zinc-400">{currentUser?.email}</p>
                    </div>
                    <button
                      onClick={handleLogout}
                      className="w-full px-4 py-2 text-left hover:bg-zinc-700 flex items-center gap-2"
                    >
                      <LogOut className="w-4 h-4" />
                      Sign Out
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <button
                onClick={() => setShowAuthModal(true)}
                className="px-6 py-2 bg-red-600 hover:bg-red-700 rounded-lg font-semibold transition-colors"
              >
                Sign In
              </button>
            )}
          </div>
        </div>
      </header>

      {/* Hidden file input for bulk selection */}
      <input
        ref={fileInputRef}
        type="file"
        accept="video/mp4,video/webm,video/quicktime,video/x-matroska"
        multiple
        className="hidden"
        onChange={(e) => handleFilesSelected(e.target.files)}
      />

      <main className="max-w-7xl mx-auto px-4 py-8">
        <h2 className="text-3xl font-bold mb-6">Videos</h2>

        {videos.length === 0 && !loadingMore ? (
          <div className="text-center py-20 text-zinc-400">
            <Video className="w-16 h-16 mx-auto mb-4 opacity-50" />
            <p>No videos available yet</p>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {videos.map(video => (
                <VideoCard key={video.id} video={video} />
              ))}
            </div>

            {/* Infinite-scroll sentinel + loading state */}
            {hasMore && (
              <div ref={loaderRef} className="flex justify-center py-10 text-zinc-500">
                {loadingMore ? 'Loading more…' : ''}
              </div>
            )}
            {!hasMore && videos.length > 0 && (
              <div className="text-center py-10 text-zinc-600 text-sm">
                You've reached the end.
              </div>
            )}
          </>
        )}
      </main>

      {showAuthModal && <AuthModal />}
      {showUploadModal && (
        <BulkUploadModal
          queuedVideos={queuedVideos}
          expandedVideos={expandedVideos}
          fileInputRef={fileInputRef}
          summary={uploadSummary}
          onClose={() => { setShowUploadModal(false); setQueuedVideos([]); setUploadSummary(null); }}
          onDismissSummary={() => setUploadSummary(null)}
          onSelectMore={() => fileInputRef.current?.click()}
          onFilesDropped={handleFilesSelected}
          onUpdateMetadata={updateVideoMetadata}
          onAddTag={addTagToVideo}
          onRemoveTag={removeTagFromVideo}
          onRemoveVideo={removeVideoFromQueue}
          onToggleExpand={toggleVideoExpanded}
          onUploadAll={uploadAllVideos}
          onCancelUpload={cancelUpload}
        />
      )}
      {showPlayerModal && selectedVideo && (
        <VideoPlayerModal
          video={selectedVideo}
          onClose={() => setShowPlayerModal(false)}
          copiedId={copiedId}
          onCopy={copyToClipboard}
        />
      )}
      {showEditModal && editingVideo && (
        <EditModal
          editingVideo={editingVideo}
          editTags={editTags}
          editIsPublic={editIsPublic}
          loading={loading}
          error={error}
          onClose={() => setShowEditModal(false)}
          onSave={handleEditVideo}
          onAddTag={addEditTag}
          onRemoveTag={removeEditTag}
          onToggleIsPublic={setEditIsPublic}
        />
      )}
    </div>
  );
};

export default App;
